/**
 * WF-2008: notice for visitors whose browser blocks Cookiebot (ad blocker, VPN filter, company proxy).
 * Without uc.js there is no banner, so the visitor can never give consent.
 *
 * The texts come from templates/_includes/cookiebotBlockedNotice.twig. head-meta.twig renders them
 * only next to the Cookiebot script. A page without the templates thus never shows a notice.
 */

const NOTICE_TEMPLATE_ID = 'cookiebot-blocked-notice';
const DISMISSED_KEY = 'heroCookiebotBlockedNoticeDismissed';
// --breakpoint-sm in src/less/tailwind.css. Below it the sticky demo button spans the bottom edge.
const SM_QUERY = '(min-width: 640px)';

/**
 * Tells if uc.js did not load. Call it only after uc.js loaded or failed. uc.js loads synchronously
 * in the head, so the result is final when the module scripts run.
 *
 * Do not test for `typeof window.Cookiebot === 'undefined'`. The script tag has `id="Cookiebot"`, and
 * the browser makes each element with an id available on `window`. After a block,
 * `window.Cookiebot` is thus the <script> element.
 * @returns {boolean}
 */
export function isCookiebotBlocked() {
    return typeof window.Cookiebot?.renew !== 'function';
}

function readDismissed() {
    try {
        return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
        return false;
    }
}

function writeDismissed() {
    try {
        window.sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
        // Restricted storage: the notice comes back on the next page.
    }
}

/**
 * On mobile the sticky demo button covers the bottom of the viewport. The bar goes above it.
 * The template uses the variable only below sm. From sm the bar has a fixed offset.
 * @param {HTMLElement} bar
 */
function updateLift(bar) {
    const demoButton = document.getElementById('signup-demo-fixed');
    const visible = demoButton && window.getComputedStyle(demoButton).display !== 'none';
    bar.style.setProperty('--cookiebot-blocked-lift', visible ? `${demoButton.offsetHeight}px` : '0px');
}

/**
 * Shows the notice bar at the bottom of the viewport.
 * @param {boolean} force true after a click on locked content. Then the bar also shows after a dismissal.
 * @returns {HTMLElement|null} the bar, or null if the page has no template
 */
export function showCookiebotBlockedNotice(force = false) {
    let bar = document.querySelector('.cookiebot-blocked-notice');
    if (!bar) {
        if (!force && readDismissed()) {
            return null;
        }
        const template = document.getElementById(NOTICE_TEMPLATE_ID);
        if (!template) {
            return null;
        }
        bar = template.content.firstElementChild.cloneNode(true);
        // A rotation or resize across sm shows or hides the demo button. The lift must follow.
        const smQuery = window.matchMedia?.(SM_QUERY);
        const onBreakpointChange = () => updateLift(bar);
        smQuery?.addEventListener('change', onBreakpointChange);
        bar.querySelector('.cookiebot-blocked-notice-close')?.addEventListener('click', () => {
            writeDismissed();
            smQuery?.removeEventListener('change', onBreakpointChange);
            bar.remove();
            // Keyboard users continue at the content that opened the bar.
            if (bar.opener?.isConnected) {
                bar.opener.focus();
            }
        });
        updateLift(bar);
        document.body.appendChild(bar);
    }
    if (force) {
        if (document.activeElement !== bar && !bar.contains(document.activeElement)) {
            bar.opener = document.activeElement;
        }
        bar.focus();
    }
    return bar;
}

/**
 * Opens the Cookiebot dialog. If Cookiebot is blocked, it shows the notice instead.
 */
export function renewCookieConsent() {
    if (isCookiebotBlocked()) {
        showCookiebotBlockedNotice(true);
        return;
    }
    window.Cookiebot.renew();
}

const LOCKED_IFRAME = 'iframe[data-cookieconsent][data-src]:not([src])';

/**
 * Hides each rich-text iframe that waits for consent. Cookiebot copies `data-src` to `src` after
 * consent. Without Cookiebot the iframe stays an empty box. The rich text has its own
 * cookieconsent-optout-marketing box next to each such iframe, and that box tells the visitor why.
 */
function hideLockedIframes() {
    document.querySelectorAll(LOCKED_IFRAME).forEach((iframe) => {
        iframe.classList.add('hidden');
    });
}

export default function initCookiebotBlocked() {
    // The consent links of the templates carry data-cookiebot-renew. The rich text has its own links
    // with `javascript: Cookiebot.renew()`, which throw after a block. preventDefault stops that URL.
    document.addEventListener('click', (e) => {
        const link = e.target.closest?.('[data-cookiebot-renew], a[href*="Cookiebot.renew"]');
        if (!link) {
            return;
        }
        e.preventDefault();
        renewCookieConsent();
    });

    if (!isCookiebotBlocked() || !document.getElementById(NOTICE_TEMPLATE_ID)) {
        return;
    }
    hideLockedIframes();
    showCookiebotBlockedNotice();
}
